import React, { FC } from 'react';

import { TimeRange } from '@grafana/data';
import { Tooltip, useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';

import { DurationDisplay } from 'components/DurationDisplay';
import { Investigation } from 'types';

// Component Props Interface
interface Props {
  title: string;
  investigation?: Investigation;
}

// Declare Component
export const Title: FC<Props> = ({ title, investigation }) => {
  const styles = useStyles2(getStyles);

  const labels = (investigation?.requestData?.labels ?? {}) as Record<string, string>;
  const labelText = Object.keys(labels).map((label, index) => {
    return (
      <>
        <span className={styles.basic}>{label}:</span>
        <span className={styles.heavy}>{labels[label]}</span>
        {index < Object.keys(labels).length - 1 ? <span className={styles.basic}>,</span> : null}
      </>
    );
  });

  return (
    <div>
      <div className={styles.title}>{title}</div>
      <TimeRangeDisplay timeRange={investigation?.timeRange} />
      <div>{labelText}</div>
    </div>
  );
};

interface TimeRangeDisplayProps {
  timeRange?: TimeRange;
}

export const TimeRangeDisplay: FC<TimeRangeDisplayProps> = ({ timeRange }) => {
  const styles = useStyles2(getStyles);

  // if we have no time range, do not display anything
  if (timeRange === undefined) {
    return null;
  }

  // define times to be displayed in users time zone
  const timeStartUTCString = timeRange.from.utc().toISOString();
  const timeStartLocal = new Date(timeStartUTCString);

  const timeEndUTCString = timeRange.to.utc().toISOString();
  const timeEndLocal = new Date(timeEndUTCString);

  // get users time zone abbreviation
  const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className={styles.inline}>
      <span className={styles.basic}>Range ({timeZoneName}):</span>
      <span className={styles.heavy}>
        <Tooltip theme="error" content={`UTC: ${timeStartUTCString}`}>
          <span className={styles.pointer}>{timeStartLocal.toLocaleString()}</span>
        </Tooltip>
        {' - '}
        <Tooltip theme="error" content={`UTC: ${timeEndUTCString}`}>
          <span className={styles.pointer}>{timeEndLocal.toLocaleString()}</span>
        </Tooltip>
      </span>
      {timeRange !== undefined ? (
        <span className={styles.duration}>
          <DurationDisplay
            startTime={timeRange.from.toISOString()}
            endTime={timeRange.to.toISOString()}
            iconOpacity={0.5}
          />
        </span>
      ) : null}
    </div>
  );
};

// Define CSS Styles
const getStyles = () => {
  return {
    title: css`
      padding: 0px;
      margin-bottom: 16px;
      line-height: 36px;
      font-size: 36px;
    `,
    inline: css`
      display: flex;
      align-items: center;
    `,
    basic: css`
      font-weight: normal;
      margin-right: 4px;
      opacity: 0.5;
    `,
    heavy: css`
      font-weight: bold;
    `,
    duration: css`
      margin-left: 8px;
    `,
    pointer: css`
      cursor: default;
    `,
  };
};

// Export Component
export default Title;
